import Badge from '@repo/ui/badge'
import { Column } from '@repo/ui/data-table'
import { LocationTwoIcon, PlusIcon } from '@repo/ui/icons'
import Modal, { MODAL_CONTENT_CLASSNAME } from '@repo/ui/modal'
import { Skeleton } from '@repo/ui/skeleton'
import { useMutation } from '@tanstack/react-query'
import { useRef } from 'react'
import { toast } from 'sonner'

import { API, constructApiUrl } from '../../../api/apis.config'
import { createProductJourneyPlace } from '../../../api/products/create-product-journey-place'
import { getProduct } from '../../../api/products/get-product'
import { useDataProps } from '../../../lib/hooks/use-data-props'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { Product } from '../../../lib/types/models/product/product'
import { ProductJourneyPlace } from '../../../lib/types/models/product/product-journey-place'
import { DataProps } from '../../../lib/types/utils'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { getTranslatedName } from '../../../lib/utils/functions/translation.functions'
import { DynamicTableRef } from '../../elements/dynamic-table'
import NestedDataTable from '../../elements/nested-table'
import ProductJourneyPlaceForm from './product-journey-place-form'

const columns: Column<ProductJourneyPlace>[] = [
  {
    accessorKey: 'place.name',
    header: 'Lieu de séjour',
    enableSorting: true,
    cell: ({ row }) => row.original.place.name
  },
  {
    accessorKey: 'weeklyPrice',
    header: 'Frais de séjour / semaine',
    enableSorting: true,
    cell: ({ row }) => `${row.original.weeklyPrice}€ / sem`
  },
  {
    accessorKey: 'option.translations',
    header: 'Option',
    cell: ({ row }) => (
      <Badge
        text={`${getTranslatedName(row.original.option)} (${row.original.optionPrice}€ / sem)`}
      />
    )
  },
  {
    accessorKey: 'mandatoryUnderAge',
    header: 'Obligatoire < âge',
    enableSorting: true,
    cell: ({ row }) =>
      row.original.hasAgeLimit && row.original.mandatoryUnderAge
        ? `<${row.original.mandatoryUnderAge}`
        : '-'
  }
]

const ProductJourneyPlacesTable = (props: DataProps<Product>) => {
  const { id: productId, data: product } = useDataProps(props, (id) => getProduct(id))

  const tableRef = useRef<DynamicTableRef<ProductJourneyPlace>>(null)

  const { modalState, toggleModal, closeModel } = useModalManager<'create-journey-place'>([
    'create-journey-place'
  ])

  const { mutateAsync: createProductJourneyPlaceMutation } = useMutation({
    mutationFn: createProductJourneyPlace,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      closeModel('create-journey-place')
      toast.success('Lieu de séjour créé avec succès')
    }
  })

  if (!product) return <Skeleton className="h-52 w-full" />

  return (
    <>
      <NestedDataTable<ProductJourneyPlace, null>
        ref={tableRef}
        variant="action"
        title="Lieux de Séjours"
        columns={columns}
        endpoint={constructApiUrl(API.products.getProductJourneyPlaces.url, { productId })}
        deleteMessage={'Voulez-vous vraiment supprimer ce lieu de séjour ?'}
        onDelete={() => toast.success('Lieu de séjour supprimé avec succès')}
        paginated={false}
        limitControl
        actions={[
          {
            icon: PlusIcon,
            variant: 'primary',
            label: 'Ajouter un lieu de séjour',
            fn: () => toggleModal('create-journey-place')
          }
        ]}
      />

      <Modal
        isOpen={modalState['create-journey-place']}
        title={'Ajouter un lieu de séjour'}
        icon={LocationTwoIcon}
        hideActions
        size="medium"
        onCancel={() => closeModel('create-journey-place')}
      >
        <ProductJourneyPlaceForm
          className={MODAL_CONTENT_CLASSNAME}
          product={product}
          buttonsConfig={modalButtonConfig({
            onConfirm: async (values) =>
              await createProductJourneyPlaceMutation({ ...values, productId }),
            onCancel: () => closeModel('create-journey-place')
          })}
        />
      </Modal>
    </>
  )
}

export default ProductJourneyPlacesTable
